import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { ConfirmDialogComponent } from '../../../components/confirm-dialog/confirm-dialog.component';
import { ActivatedRoute, Router } from '@angular/router';
import { HelpersService } from '../../../core/services/helpers.service';
import { MatDialog } from '@angular/material/dialog';
import { TagService } from '../../../core/api/tag.service';
import { ExpanseCategoryService } from '../../../core/api/expanse-category.service';
import { FileService } from '../../../core/api/file.service';
import { LoadingService } from '../../../core/services/loading.service';
import { AuthService } from '../../../core/services/auth.service';
import { UserService } from '../../../core/api/user.service';
import moment from 'moment';
import {TeamService} from '../../../core/api/team.service';
import { PositionService } from '../../../core/api/position.service';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss',
})
export class ProfileComponent {
  m: any;
  id: any;
  form: FormGroup;
  tag: any[] = [];
  expenseCategory: any[] = [];
  uploadStatus: boolean = false;
  fileExists: boolean = false;
  fileUrl: any;
  file: any;
  fileName: string = '';
  imageUrl: any;
  editForm: boolean = false;
  currentUser: any;
  profileData: any = null;
  teams: any[] = [];
  positions: any[] = [];

  constructor(
    private activatedRoute: ActivatedRoute,
    private helpers: HelpersService,
    private fb: FormBuilder,
    private router: Router,
    private dialog: MatDialog,
    private tagService: TagService,
    private expanseCategoryService: ExpanseCategoryService,
    private fileService: FileService,
    private loadingService: LoadingService,
    private authService: AuthService,
    private userService: UserService,
    private teamService: TeamService,
    private positionService: PositionService,
  ) {
    this.m = this.activatedRoute.snapshot.queryParams['m'];
    this.id = this.activatedRoute.snapshot.queryParams['id'];
    this.currentUser = this.authService.getLoggedUser();
    this.form = this.fb.group({
      first_name: [null, [Validators.required]],
      last_name: [null, [Validators.required]],
      gender: ['Laki-laki', [Validators.required]],
      identity_number: [null, [Validators.required]],
      date_birth: [null, [Validators.required]],
      place_birth: [null, [Validators.required]],
      email: [null, [Validators.required]],
      phone: [null, [Validators.required]],
      employee_id: [null, [Validators.required]],
      position: [null, [Validators.required]],
      statusEmployment: [null, [Validators.required]],
      teamId: [null, [Validators.required]],
      join_date: [null, [Validators.required]],
      end_date: [null],
      roles: [null, [Validators.required]],
      bank_name: [null],
      bank_account_name: [null],
      bank_account_number: [null],
      max_amount_cash_advance: [null],
      password: [null],
      re_enter_password: [null],
      fileId: [null],
    });
    this.form.disable({ emitEvent: false });

    // Jalankan semua proses asynchronous secara paralel
    this.loadingService.startLoading();
    const tagPromise = this.getTag();
    const expanseCategoryPromise = this.getExpanseCategory();
    const profilePromise = this.getCurrentProfile();
    const positionsPromise = this.getPositions();

    Promise.all([tagPromise, expanseCategoryPromise, profilePromise, this.getTeams(), positionsPromise]).finally(
      () => {
        this.loadingService.endLoading();
      }
    );
  }

  getTag(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.tagService.getAllData().then(
        (res: any) => {
          this.tag = res;
          resolve();
        },
        (error: any) => {
          this.helpers.sbError(error.message);
          reject(error);
        }
      );
    });
  }

  getTeams(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.teamService.getAllData().then(
        (res) => {
          this.teams = res;
          resolve();
        },
        (error) => {
          this.helpers.sbError(error.message);
          reject(error);
        }
      );
    });
  }

  getPositions(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.positionService.getAllData().then(
        (res) => {
          this.positions = res;
          this.ensurePositionOption(this.form.get('position')?.value);
          resolve();
        },
        (error) => {
          this.helpers.sbError(error.message);
          reject(error);
        }
      );
    });
  }

  getExpanseCategory(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.expanseCategoryService.getAllData().then(
        (res: any) => {
          this.expenseCategory = res.filter(
            (item: any) => item.type == 'Reimbursment'
          );
          resolve();
        },
        (error: any) => {
          this.helpers.sbError(error.message);
          reject(error);
        }
      );
    });
  }

  getCurrentProfile(): Promise<void> {
    return new Promise((resolve, reject) => {
      const userId = this.currentUser?.id;
      if (!userId) {
        resolve();
        return;
      }
      this.userService.getById(userId).then(
        (data: any) => {
          this.profileData = data;
          this.applyProfileToForm(data);
          resolve();
        },
        (error: any) => {
          this.helpers.sbError(error.message);
          reject(error);
        }
      );
    });
  }

  private applyProfileToForm(data: any): void {
    const patchValue: any = {
      first_name: data?.firstName ?? null,
      last_name: data?.lastName ?? null,
      gender: this.normalizeGender(data?.gender),
      identity_number: data?.identityNumber ?? null,
      date_birth: data?.birthDate
        ? moment(data.birthDate).format('YYYY-MM-DD')
        : null,
      place_birth: data?.placeOfBirth ?? null,
      email: data?.email ?? null,
      phone: data?.phone ?? null,
      employee_id: data?.employeeId ?? null,
      position: data?.position ?? null,
      statusEmployment: data?.statusEmployment ?? null,
      teamId:
        data?.team?.id != null ? Number(data.team.id) : null,
      join_date: data?.joinDate
        ? moment(data.joinDate).format('YYYY-MM-DD')
        : null,
      end_date: data?.endDate
        ? moment(data.endDate).format('YYYY-MM-DD')
        : null,
      roles: this.getRolesLabelFromData(data),
      bank_name: data?.bankName ?? null,
      bank_account_name: data?.bankAccountName ?? null,
      bank_account_number: data?.bankAccountNumber ?? null,
      max_amount_cash_advance: data?.maximumAmount ?? null,
      password: null,
      re_enter_password: null,
    };
    this.ensurePositionOption(data?.position ?? null);

    if (data?.file) {
      this.file = data.file;
      this.fileExists = true;
      this.form.get('fileId')?.setValue(data.file.id);
      this.fileName = data.file.fileName;
      this.loadingService.startLoading();
      this.fileService.download(data.file.id).subscribe(
        (res: any) => {
          this.loadingService.endLoading();
          this.imageUrl = URL.createObjectURL(res);
        },
        () => {
          this.loadingService.endLoading();
        }
      );
    }

    this.form.patchValue(patchValue);
  }

  getFullName(): string {
    const firstName = this.profileData?.firstName ?? '';
    const lastName = this.profileData?.lastName ?? '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || '-';
  }

  getRolesLabel(): string {
    return this.getRolesLabelFromData(this.profileData);
  }

  private getRolesLabelFromData(data: any): string {
    if (!data?.userRoles?.length) return '-';
    return data.userRoles
      .map((item: any) => item?.role?.name)
      .filter(Boolean)
      .join(', ');
  }

  formatDate(value: any): string {
    if (!value) return '-';
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format('D MMMM YYYY') : '-';
  }

  private normalizeGender(value?: string | null): string | null {
    if (!value) return null;
    const normalized = value.trim().toLowerCase();
    if (['male', 'laki-laki', 'l'].includes(normalized)) return 'Laki-laki';
    if (['female', 'perempuan', 'p'].includes(normalized)) return 'Perempuan';
    return value;
  }

  private ensurePositionOption(positionName?: string | null): void {
    if (!positionName) return;
    const exists = this.positions.some((item: any) => item.name === positionName);
    if (!exists) {
      this.positions = [
        ...this.positions,
        {
          id: `legacy-${positionName}`,
          name: positionName,
          description: 'Legacy position',
        },
      ];
    }
  }

  enableEdit(): void {
    this.editForm = true;
    this.form.enable({ emitEvent: false });
    this.form.get('roles')?.disable({ emitEvent: false });
    ['gender', 'position', 'teamId'].forEach((key) => {
      this.form.get(key)?.enable({ emitEvent: false });
    });
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  cancelEdit(): void {
    this.editForm = false;
    this.form.disable({ emitEvent: false });
    if (this.profileData) {
      this.applyProfileToForm(this.profileData);
    }
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  submit() {
    this.form.markAllAsTouched();
    if (
      this.form.valid &&
      (this.form.get('password')?.value || '') ===
        (this.form.get('re_enter_password')?.value || '')
    ) {
      const data = this.form.getRawValue();
      data['rolesId'] = [];
      this.dialog
        .open(ConfirmDialogComponent, {
          width: '545px',
          maxHeight: '95vh',
          panelClass: 'confirm-dialog',
          data: {
            title: 'Edit Profile',
            message: `Are you sure you want to proceed?`,
          },
        })
        .afterClosed()
        .subscribe((result: any) => {
          if (result) {
            this.loadingService.startLoading();
            this.userService.updateById(this.currentUser.id, data).then(() => {
              this.loadingService.endLoading();
              this.helpers.sbSuccess('Profile Updated!');
              this.editForm = false;
              this.form.disable({ emitEvent: false });
              this.getCurrentProfile();
            }).catch((error: any) => {
              this.loadingService.endLoading();
              this.helpers.sbError(error.message);
            })
          }
        });
    } else if (this.form.get('fileId')?.invalid) {
      this.helpers.sbError(
        'Please upload file firstly and fill all required fields!'
      );
    } else {
      this.helpers.sbError('Lengkapi form terlebih dahulu!');
    }
  }

  uploadFile(event: any) {
    this.uploadStatus = true;
    if (event.target.files.length) {
      const file = event.target.files[0];
      this.loadingService.startLoading();
      try {
        this.fileService.upload(file).subscribe(
          (res: any) => {
            this.loadingService.endLoading();
            if (res?.body?.data) {
              const fileId = res?.body?.data?.id;
              this.file = res?.body?.data;
              this.form.get('fileId')?.setValue(fileId);
              this.fileExists = true;
              this.uploadStatus = false;
              this.fileName = file.name;
              this.loadingService.startLoading();
              this.fileService.download(fileId).subscribe(
                (downloadRes: any) => {
                  this.loadingService.endLoading();
                  this.imageUrl = URL.createObjectURL(downloadRes);
                },
                () => {
                  this.loadingService.endLoading();
                }
              );
            }
          },
          (err: any) => {
            this.loadingService.endLoading();
            this.helpers.sbError(err.message);
            this.uploadStatus = false;
          }
        );
      } catch (err: any) {
        this.loadingService.endLoading();
        this.helpers.sbError(err.message);
        this.uploadStatus = false;
      }
    } else {
      this.helpers.sbError('Tidak ada file yang akan diupload!');
      this.uploadStatus = false;
    }
  }
}
